import { useEffect, useState, useId } from 'react';
import ToolTip from 'components/tooltip';
import IconButton, {
	type IProps as IconButtonProps,
} from 'components/controls/buttons/icon-button';
import ChevronDownIcon from 'assets/icons/chevron-down.svg';
import styles from './buttons.module.scss';

export function Button({
	label,
	icon = null,
	className,
	showLabel,
	disabled,
	...restProps
}: ButtonProps) {
	const classNames = [styles.button];
	if (className) classNames.push(className);
	if (showLabel) classNames.push(styles.labelButton);

	return (
		<IconButton
			label={label}
			icon={icon}
			className={classNames.join(' ')}
			showLabel={showLabel}
			disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			{...restProps}
		/>
	);
}

export type ButtonProps = IconButtonProps & {
	icon?: React.ReactNode;
};

export function MenuButton({
	menuContents,
	label,
	icon,
	disabled,
	onOpenOrClose,
	...restProps
}: MenuButtonProps) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const id = useId();

	useEffect(() => {
		onOpenOrClose?.(isMenuOpen);
	}, [isMenuOpen, onOpenOrClose]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			e.stopImmediatePropagation();

			// Close the menu. We could use a ref to a specific element in the body but this is simpler.
			if (e.key === 'Escape') {
				document.body.click();
			}
		};

		if (isMenuOpen) {
			document.addEventListener('keydown', handleKeyDown, true);
		} else {
			document.removeEventListener('keydown', handleKeyDown, true);
		}

		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, [isMenuOpen]);

	const handleOpenOrClose = (isOpen: boolean) => {
		if (disabled) return;
		setIsMenuOpen(isOpen);
	};

	const arrowIconStyles = [styles.arrowIcon];
	if (isMenuOpen) {
		arrowIconStyles.push(styles.open);
	}

	return (
		<ToolTip
			id={id}
			jsxDescription={menuContents}
			onOpenOrClose={handleOpenOrClose}
			clickToOpen={true}
		>
			<Button
				jsxLabel={
					<>
						<span>{label}</span>
						<ChevronDownIcon className={arrowIconStyles.join(' ')} />
					</>
				}
				icon={icon}
				className={styles.menuTrigger}
				showLabel={true}
				aria-haspopup="listbox"
				aria-expanded={isMenuOpen}
				disabled={disabled}
				{...restProps}
			/>
		</ToolTip>
	);
}

interface MenuButtonProps extends ButtonProps {
	menuContents: React.ReactNode;
	onOpenOrClose?: (isOpen: boolean) => void;
}
